'use strict';

const { showToastMessage } = require('progamers_core/components/toast');

/**
 * Marks the wishlist heart as active if the product is in the wishlist.
 * @param {HTMLElement} productElement - The product tile element.
 */
function updateWishlistHeartForProduct(productElement) {
    var wishlistItems = window.wishlist ? window.wishlist.items : [];
    var pid = productElement.dataset.pid;
    if (wishlistItems.length > 0) {
        wishlistItems.forEach(function (item) {
            if (item.pid === pid) {
                productElement.querySelector('.wishlist-tile')?.classList.add('added');
            }
        });
    }
}

/**
 * Updates all wishlist hearts on the page to reflect current wishlist state.
 */
function updateAllWishlistHearts() {
    document.querySelectorAll('.product').forEach(function (product) {
        updateWishlistHeartForProduct(product);
    });
}

/**
 * Updates the wishlist quantity indicator in the header.
 */
function updateWishlistCounter() {
    const wishlistQuantityIndicatorEle = document.querySelector('.js-wishlist-quantity-indicator');
    if (!wishlistQuantityIndicatorEle) {
        return;
    }

    const count = window.wishlist?.items?.length ?? 0;
    if (count > 0) {
        wishlistQuantityIndicatorEle.innerText = `${count}`;
        wishlistQuantityIndicatorEle.classList.remove('d-none');
    } else {
        wishlistQuantityIndicatorEle.classList.add('d-none');
    }
}

/**
 * Updates the wishlist heart icon and shows a toast after an add/remove action.
 * @param {Object} data - The server response data.
 * @param {HTMLElement} tile - The wishlist tile element.
 */
function displayMessageAndChangeIcon(data, tile) {
    tile.classList.remove('processing');

    if (data.success && data.event === 'add') {
        window.wishlist.items.push({ pid: data.pid });
        tile.classList.add('added');
    } else if (data.success && data.event === 'remove') {
        tile.classList.remove('added');
        window.wishlist.items = window.wishlist.items.filter(item => item.pid !== data.pid);
    }

    updateWishlistCounter();

    showToastMessage({
        message: data.msg,
        alertType: data.success ? 'alert-success' : 'alert-danger'
    });

    document.querySelectorAll('[data-pid="' + data.pid + '"]').forEach(function (product) {
        const heart = product.querySelector('.wishlist-tile');
        if (heart) {
            if (data.event === 'add') {
                heart.classList.add('added');
            } else {
                heart.classList.remove('added');
            }
        }
    });
}

/**
 * Attaches delegated click handler to wishlist heart tiles for add/remove actions.
 */
function addToWishlistHeart() {
    document.body.addEventListener('click', function (e) {
        const tile = e.target.closest('.wishlist-tile');
        if (!tile) {
            return;
        }

        e.preventDefault();
        e.stopImmediatePropagation();

        const addWishlistUrl = tile.dataset.addUrl;
        const removeWishlistUrl = tile.dataset.removeUrl;
        const pid = tile.closest('.product')?.dataset.pid;
        const productDetail = tile.closest('.product-detail');

        let optionId = productDetail?.querySelector('.product-option')?.dataset.optionId || null;
        let optionVal = productDetail?.querySelector('.options-select')?.selectedOptions[0]?.dataset.valueId || null;
        let optionRadioId = productDetail?.querySelector('.product-option-radio')?.dataset.optionId || null;
        let optionRadioVal = productDetail?.querySelector('.js-options-radio:checked')?.dataset.valueId || null;

        optionId = optionId || optionRadioId;
        optionVal = optionVal || optionRadioVal;

        if (!addWishlistUrl || !pid) {
            return;
        }

        tile.classList.add('processing');

        if (!tile.classList.contains('added')) {
            const params = new URLSearchParams({ pid });
            if (optionId) {
                params.set('optionId', optionId);
            }
            if (optionVal) {
                params.set('optionVal', optionVal);
            }

            fetch(addWishlistUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: params
            })
                .then(r => r.json())
                .then(function (data) {
                    if (typeof window.checkResponseForEventDetails === 'function') {
                        window.checkResponseForEventDetails(data);
                    }
                    data.event = 'add';
                    displayMessageAndChangeIcon(data, tile);
                })
                .catch(function (err) {
                    displayMessageAndChangeIcon(err, tile);
                });
        } else {
            if (!removeWishlistUrl || !pid) {
                return;
            }
            fetch(`${removeWishlistUrl}?${new URLSearchParams({ pid })}`)
                .then(r => r.json())
                .then(function (data) {
                    if (typeof window.checkResponseForEventDetails === 'function') {
                        window.checkResponseForEventDetails(data);
                    }
                    data.event = 'remove';
                    data.pid = pid;
                    displayMessageAndChangeIcon(data, tile);
                })
                .catch(function (err) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                });
        }
    });
}

/**
 * Polls until window.wishlist is available, then runs the callback.
 * @param {Function} callback - Function to call once wishlist data is ready.
 * @param {number} interval - Polling interval in ms.
 * @param {number} maxAttempts - Maximum number of polling attempts.
 */
function waitForWishlist(callback, interval = 100, maxAttempts = 50) {
    let attempts = 0;
    const timer = setInterval(() => {
        if (window.wishlist && window.wishlist.items) {
            clearInterval(timer);
            callback();
        } else if (attempts + 1 >= maxAttempts) {
            clearInterval(timer);
        }
        attempts += 1;
    }, interval);
}

/**
 * Listens for product:tileRendered events to update hearts on dynamically loaded tiles.
 */
function addProductTileRenderedListener() {
    document.body.addEventListener('product:tileRendered', function (e) {
        if (!window.wishlist || !window.wishlist.items) {
            return;
        }
        const scope = e.detail || document;
        scope.querySelectorAll('.product').forEach(function (product) {
            updateWishlistHeartForProduct(product);
        });
    });
}

/**
 * Initialises wishlist hearts, counter and event listeners.
 */
function init() {
    waitForWishlist(() => {
        updateWishlistCounter();
        updateAllWishlistHearts();
        addToWishlistHeart();
    });
    addProductTileRenderedListener();
}

module.exports = {
    init: init
};
